import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCookieValue } from './utils';

const formatPercent = (value, decimals = 2) => {
  if (value === null || value === undefined) return 'N/A';
  const num = Number(value);
  if (Number.isNaN(num)) return 'N/A';
  return `${num.toFixed(decimals)}%`;
};

const formatHour = (value) => {
  if (!value) return 'N/A';
  if (typeof value === 'string') {
    // Format: '2026-01-20 14:00:00' -> '14:00'
    const match = value.match(/\d{2}:\d{2}/);
    return match ? match[0] : value;
  }
  return String(value);
};

const formatHourWithDate = (value) => {
  if (!value) return 'N/A';
  if (typeof value === 'string') {
    // Format: '2026-01-20 14:00:00' -> 'Jan 20, 14:00'
    const match = value.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}:\d{2})/);
    if (match) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[parseInt(match[2]) - 1];
      const day = parseInt(match[3]);
      const time = match[4];
      return `${month} ${day}, ${time}`;
    }
  }
  return String(value);
};

const parseNumeric = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object' && value.value !== undefined) {
    return Number(value.value);
  }
  const num = Number(value);
  return Number.isNaN(num) ? null : num;
};

// Service categories
const SERVICE_CATEGORIES = {
  storefront: [
    'adrsmpr',
    'order-service',
    'search-vm',
    'customer-service',
    'customermg',
    'eas',
    'inventory',
    'mulin',
    'omsnode',
    'order-service-oms',
    'payment',
    'promonj',
    'quote-service',
    'recommendation',
    'referal',
    'remoteconf'
  ],
  other: [
    'feed-vm',
    'luna',
    'notification',
    'octa',
    'orbix',
    'sentinel',
    'sizeblock',
    'stylishop.com',
    'terra',
    'auriga'
  ]
};

export const HourlyDashboard = () => {
  const navigate = useNavigate();
  const [hourlyData, setHourlyData] = useState([]);
  const [selectedServices, setSelectedServices] = useState(['stylishop.com']);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // Date/time filter states
  const [selectedDate, setSelectedDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [useCustomRange, setUseCustomRange] = useState(false);

  const services = useMemo(() => {
    const serviceSet = new Set();
    hourlyData.forEach((row) => {
      if (row.service) serviceSet.add(row.service);
    });
    return Array.from(serviceSet).sort();
  }, [hourlyData]);

  const chartData = useMemo(() => {
    if (!Array.isArray(hourlyData) || hourlyData.length === 0) return {};

    // Group by hour - keep full datetime string
    const byHour = {};
    hourlyData.forEach((row) => {
      const hour = row.hour; // Keep full datetime: '2026-01-20 14:00:00'
      if (!byHour[hour]) {
        byHour[hour] = {};
      }
      const service = row.service;
      const success = parseNumeric(row.hourly_success_pct);
      if (success !== null) {
        byHour[hour][service] = success;
      }
    });

    // Sort hours chronologically
    const sortedHours = Object.keys(byHour).sort();

    // Build series per service
    const series = {};
    services.forEach((service) => {
      series[service] = sortedHours.map((hour) => ({
        hour,
        value: byHour[hour][service] || null
      }));
    });

    return { hours: sortedHours, series };
  }, [hourlyData, services]);

  const filteredSeries = useMemo(() => {
    if (!chartData.series || typeof chartData.series !== 'object') {
      return {};
    }
    if (selectedServices.length === 0) {
      return chartData.series;
    }
    const filtered = {};
    selectedServices.forEach((service) => {
      if (chartData.series[service]) {
        filtered[service] = chartData.series[service];
      }
    });
    return filtered;
  }, [chartData.series, selectedServices]);

  const fetchData = async () => {
    const accessToken = getCookieValue('accessToken');
    if (!accessToken) {
      navigate('/login');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');
    try {
      const params = {};
      if (useCustomRange && selectedDate && startTime && endTime) {
        params.date = selectedDate;
        params.startTime = startTime;
        params.endTime = endTime;
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_ENDPOINT}/get-hourly-slo-sli-metrics`,
        {
          headers: { Authorization: accessToken },
          params: params
        }
      );

      if (response?.data?.status) {
        setHourlyData(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setErrorMessage(response?.data?.statusMsg || 'Unable to fetch hourly SLO/SLI metrics');
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401) {
        navigate('/login');
        return;
      }
      setErrorMessage('Failed to fetch hourly SLO/SLI metrics');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 5 minutes only if not using custom range
    if (!useCustomRange) {
      const intervalId = setInterval(fetchData, 300000);
      return () => clearInterval(intervalId);
    }
  }, [useCustomRange, selectedDate, startTime, endTime]);

  const toggleService = (service) => {
    setSelectedServices((prev) => {
      if (prev.includes(service)) {
        return prev.filter((s) => s !== service);
      }
      return [...prev, service];
    });
  };

  const selectAllServices = () => {
    setSelectedServices([...services]);
  };

  const clearAllServices = () => {
    setSelectedServices([]);
  };

  const selectStorefrontServices = () => {
    const storefrontServices = services.filter(s => SERVICE_CATEGORIES.storefront.includes(s));
    setSelectedServices(storefrontServices);
  };

  const selectOtherServices = () => {
    const otherServices = services.filter(s => SERVICE_CATEGORIES.other.includes(s));
    setSelectedServices(otherServices);
  };

  const getHeaderTitle = () => {
    if (useCustomRange && selectedDate && startTime && endTime) {
      return `Hourly SLO/SLI Trend - ${selectedDate} (${startTime} to ${endTime})`;
    }
    return 'Hourly SLO/SLI Trend (Last 24 Hours)';
  };

  return (
    <div className="hourly-dashboard-container">
      <div className="hourly-dashboard-header">
        <h2>{getHeaderTitle()}</h2>
        <div className="hourly-dashboard-controls">
          <button onClick={fetchData} disabled={isLoading} style={{ padding: '4px 8px', fontSize: '12px' }}>
            Refresh
          </button>
        </div>
      </div>

      {/* Date/Time Range Filter */}
      <div className="hourly-dashboard-section" style={{ marginBottom: '12px' }}>
        <h3>Time Range Filter</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', fontSize: '12px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={useCustomRange}
              onChange={(e) => setUseCustomRange(e.target.checked)}
              style={{ marginRight: '4px' }}
            />
            Use Custom Date/Time Range
          </label>
          {useCustomRange && (
            <>
              <label style={{ fontSize: '12px' }}>
                Date:
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  style={{ marginLeft: '6px', padding: '4px 6px', fontSize: '12px' }}
                />
              </label>
              <label style={{ fontSize: '12px' }}>
                From:
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  style={{ marginLeft: '6px', padding: '4px 6px', fontSize: '12px' }}
                />
              </label>
              <label style={{ fontSize: '12px' }}>
                To:
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  style={{ marginLeft: '6px', padding: '4px 6px', fontSize: '12px' }}
                />
              </label>
              <button 
                onClick={fetchData} 
                disabled={!selectedDate || !startTime || !endTime || isLoading}
                style={{ 
                  padding: '4px 12px', 
                  fontSize: '12px', 
                  backgroundColor: '#FF9800', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '3px',
                  cursor: (!selectedDate || !startTime || !endTime || isLoading) ? 'not-allowed' : 'pointer',
                  opacity: (!selectedDate || !startTime || !endTime || isLoading) ? 0.6 : 1
                }}
              >
                Apply
              </button>
            </>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="hourly-dashboard-loading">Loading hourly metrics...</div>
      )}
      {errorMessage && (
        <div className="hourly-dashboard-error">{errorMessage}</div>
      )}

      <div className="hourly-dashboard-section">
        <h3>Service Filter</h3>
        <div className="hourly-service-filter">
          <button onClick={selectAllServices} style={{ padding: '4px 8px', fontSize: '11px', marginRight: '8px' }}>
            Select All
          </button>
          <button onClick={clearAllServices} style={{ padding: '4px 8px', fontSize: '11px', marginRight: '8px' }}>
            Clear All
          </button>
          <button onClick={selectStorefrontServices} style={{ padding: '4px 8px', fontSize: '11px', marginRight: '8px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '3px' }}>
            Storefront Services
          </button>
          <button onClick={selectOtherServices} style={{ padding: '4px 8px', fontSize: '11px', marginRight: '8px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '3px' }}>
            Other Services
          </button>
          <div className="hourly-service-checkboxes">
            {services.map((service) => (
              <label key={service} style={{ marginRight: '12px', fontSize: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selectedServices.includes(service)}
                  onChange={() => toggleService(service)}
                  style={{ marginRight: '4px' }}
                />
                {service}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="hourly-dashboard-section">
        <h3>Success Rate Trend</h3>
        {hoveredPoint && (
          <div className="hourly-tooltip">
            <strong>{hoveredPoint.service}</strong><br />
            {formatHourWithDate(hoveredPoint.hour)}<br />
            Success Rate: <strong>{hoveredPoint.value.toFixed(2)}%</strong>
          </div>
        )}
        <div className="hourly-chart-wrapper">
          <svg 
            className="hourly-chart" 
            viewBox="0 0 1000 420" 
            preserveAspectRatio="xMidYMid meet"
            onMouseLeave={() => setHoveredPoint(null)}
          >
            {/* Y-axis labels */}
            <text x="30" y="50" fontSize="12" fill="#555">100%</text>
            <text x="30" y="150" fontSize="12" fill="#555">75%</text>
            <text x="30" y="250" fontSize="12" fill="#555">50%</text>
            <text x="30" y="350" fontSize="12" fill="#555">25%</text>

            {/* Grid lines */}
            <line x1="60" y1="50" x2="980" y2="50" stroke="#eee" strokeWidth="1" />
            <line x1="60" y1="150" x2="980" y2="150" stroke="#eee" strokeWidth="1" />
            <line x1="60" y1="250" x2="980" y2="250" stroke="#eee" strokeWidth="1" />
            <line x1="60" y1="350" x2="980" y2="350" stroke="#eee" strokeWidth="1" />

            {/* X-axis */}
            <line x1="60" y1="360" x2="980" y2="360" stroke="#333" strokeWidth="2" />
            {/* Y-axis */}
            <line x1="60" y1="40" x2="60" y2="360" stroke="#333" strokeWidth="2" />

            {/* Plot lines and points for each service */}
            {filteredSeries && Object.entries(filteredSeries).map(([service, dataPoints], serviceIdx) => {
              const color = `hsl(${(serviceIdx * 137) % 360}, 70%, 50%)`;

              return (
                <g key={service}>
                  {/* Draw lines connecting consecutive non-null points */}
                  {dataPoints.map((dp, hourIdx) => {
                    if (hourIdx === 0 || dp.value === null) return null;
                    const prevDp = dataPoints[hourIdx - 1];
                    if (prevDp.value === null) return null;

                    const chartWidth = 980 - 60;
                    const barGroupWidth = chartWidth / (chartData.hours.length || 1);
                    const x1 = 60 + (barGroupWidth * (hourIdx - 1)) + (barGroupWidth / 2);
                    const y1 = 360 - ((prevDp.value / 100) * 310);
                    const x2 = 60 + (barGroupWidth * hourIdx) + (barGroupWidth / 2);
                    const y2 = 360 - ((dp.value / 100) * 310);

                    return (
                      <line
                        key={hourIdx}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={color}
                        strokeWidth="3"
                      />
                    );
                  })}
                  {/* Draw circles for data points */}
                  {dataPoints.map((dp, hourIdx) => {
                    if (dp.value === null) return null;
                    const chartWidth = 980 - 60;
                    const barGroupWidth = chartWidth / (chartData.hours.length || 1);
                    const x = 60 + (barGroupWidth * hourIdx) + (barGroupWidth / 2);
                    const y = 360 - ((dp.value / 100) * 310);
                    const isHovered = hoveredPoint && hoveredPoint.service === service && hoveredPoint.hour === dp.hour;

                    return (
                      <circle
                        key={hourIdx}
                        cx={x}
                        cy={y}
                        r={isHovered ? 8 : 6}
                        fill={color}
                        stroke={isHovered ? '#333' : '#fff'}
                        strokeWidth={isHovered ? 3 : 2}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => setHoveredPoint({ service, hour: dp.hour, value: dp.value })}
                      />
                    );
                  })}
                </g>
              );
            })}

            {/* X-axis labels - show date and time for each hour */}
            {chartData.hours && chartData.hours.map((hour, i) => {
              const chartWidth = 980 - 60;
              const barGroupWidth = chartWidth / (chartData.hours.length || 1);
              const x = 60 + (barGroupWidth * i) + (barGroupWidth / 2);
              
              // Parse hour: '2026-01-20 14:00:00' -> 'Jan 20' and '14:00'
              const match = hour.match(/(\d{4})-(\d{2})-(\d{2})\s+(\d{2}:\d{2})/);
              let dateLabel = '';
              let timeLabel = '';
              
              if (match) {
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const month = months[parseInt(match[2]) - 1];
                const day = parseInt(match[3]);
                const time = match[4];
                dateLabel = `${month} ${day}`;
                timeLabel = time;
              } else {
                timeLabel = formatHour(hour);
              }
              
              // Show every label if less than 15 hours, otherwise show every 2nd or 3rd
              const totalHours = chartData.hours.length;
              const showEvery = totalHours <= 12 ? 1 : totalHours <= 18 ? 2 : 3;
              const showLabel = i % showEvery === 0;
              
              if (!showLabel) return null;
              
              return (
                <g key={i}>
                  <text x={x} y="378" fontSize="10" fill="#555" textAnchor="middle" fontWeight="600">
                    {timeLabel}
                  </text>
                  <text x={x} y="392" fontSize="9" fill="#777" textAnchor="middle">
                    {dateLabel}
                  </text>
                </g>
              );
            })}
          </svg>
          <div className="hourly-chart-legend">
            {filteredSeries && Object.entries(filteredSeries).map(([service], idx) => {
              const color = `hsl(${(idx * 137) % 360}, 70%, 50%)`;
              return (
                <div key={service} style={{ display: 'inline-block', marginRight: '16px', fontSize: '11px' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: '12px',
                      height: '12px',
                      backgroundColor: color,
                      marginRight: '4px',
                      verticalAlign: 'middle'
                    }}
                  />
                  {service}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
